import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { Filter, organizationalUnitsFilter } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { usePreferences } from "@components/preferences";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveOrganizationalUnits,
} from "@planzone/view-models";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { TreeToggle } from "@common/treeToggle";
import { useResourcesListingColumns } from "@pages/planzoneMembersPage/columns";
import { Heading } from "./heading";
import { Toolbar } from "./toolbar";
import { Tree } from "./tree";
import { Loader } from "./loader";
import { Props } from "./types";
import styles from "./styles.scss";

function ObsPage(props: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    organizationalUnits,
    isRequestPending,
    isRequestFailure,
    filter,
    totalPages,
    retrieveOrganizationalUnits,
  } = useRetrieveOrganizationalUnits();
  const isLastPageLoaded = filter.page > 0 && filter.page >= totalPages;
  // Back on the page, the units kept from before are shown again a page at a time before asking the
  // back for more: mounting them all at once froze the page.
  const [hiddenUuids, setHiddenUuids] = React.useState(() =>
    organizationalUnits.slice(filter.size).map(({ uuid }) => uuid),
  );
  const shownOrganizationalUnits = React.useMemo(() => {
    const hidden = new Set(hiddenUuids);

    return hidden.size
      ? organizationalUnits.filter(({ uuid }) => !hidden.has(uuid))
      : organizationalUnits;
  }, [organizationalUnits, hiddenUuids]);
  const isEverythingShown = hiddenUuids.length === 0;
  // Unfolding everything at once froze the page as well: the units past the first page are hidden again.
  const showFirstPageOnly = () =>
    setHiddenUuids(
      organizationalUnits.slice(filter.size).map(({ uuid }) => uuid),
    );
  const { columns, label } = useResourcesListingColumns();
  const { pushView } = useModal();
  const { state, edit } = usePreferences();
  const { obsFilter } = state;
  // The criteria go to the back like the WBS filters: a new filter reloads the tree from its first page.
  const setObsFilter = (nextObsFilter: Filter) => {
    edit({ ...state, obsFilter: nextObsFilter });
    setHiddenUuids([]);
    retrieveOrganizationalUnits({
      planzone,
      filter: { ...organizationalUnitsFilter, ...nextObsFilter },
    });
  };

  return (
    <WorkspaceContext type="planzone">
      <div className={styles.obsPage}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("obsPage.title")}</h1>
          <div className={styles.profileNavigation}>
            <AssistantShortcut />
            <Notifications />
            <ProfileNavigation />
          </div>
        </div>
        <div className={styles.main}>
          <div className={styles.menu}>
            <Toolbar filter={obsFilter} setFilter={setObsFilter} />
            <button
              type="button"
              className={styles.button}
              onClick={() =>
                pushView({
                  key: UseCases.createOrganizationalUnit,
                  data: { parent: undefined },
                })
              }
            >
              <div className={styles.iconBox}>+</div>
              <p>{t("obsPage.createRootUnit")}</p>
            </button>
          </div>
          <div className={styles.content}>
            <InfiniteScroll
              className={styles.container}
              // Also once loaded: folded units can leave nothing to scroll to reach the next page.
              canFetchOnIdle={
                !!planzone.uuid &&
                !isRequestPending &&
                !isRequestFailure &&
                (!isEverythingShown || !isLastPageLoaded)
              }
            >
              {() => (
                <div
                  className={classNames(
                    styles.table,
                    styles[`columns${columns.length}`],
                  )}
                >
                  {/* The heading folds or unfolds the whole tree. */}
                  <TreeToggle>
                    <Heading
                      columns={columns}
                      label={label}
                      unfoldAll={showFirstPageOnly}
                    />
                    <Tree
                      organizationalUnits={shownOrganizationalUnits}
                      columns={columns}
                    />
                  </TreeToggle>
                  {isRequestPending && <Loader />}
                  <Flag
                    name="organizationalUnitsFlag"
                    retrieveItems={() =>
                      !isEverythingShown
                        ? setHiddenUuids((uuids) => uuids.slice(filter.size))
                        : !!planzone.uuid &&
                          !isRequestPending &&
                          retrieveOrganizationalUnits({
                            planzone,
                            filter: { ...filter, ...obsFilter },
                          })
                    }
                    isDisabled={
                      isEverythingShown &&
                      (!planzone.uuid ||
                        isRequestPending ||
                        (filter.page > 0 &&
                          totalPages > 0 &&
                          filter.page >= totalPages))
                    }
                  />
                </div>
              )}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    </WorkspaceContext>
  );
}

export { ObsPage };
